/* ------------------------------------------------------------------------------------------------------------------
   SIZE RECOMMENDATION
   Relabels Kleep's "Find your size" button as "Size S recommended (36 FR)" once the shopper has
   added to cart a product of the same sizing family. The size guide remembers the size in
   localStorage (component-size-guide.js, window.SizeGuideMemory); this element resolves it
   against its own product's size matrix — the guide rendered on the page for that product
   handle — and puts that text inside the Kleep button it wraps. The button stays Kleep's: same
   markup, same click, same widget. Kleep's own text spans ("Find your size" and, once the widget
   has its own recommendation, "Find your size: 34") are hidden through the --active class
   (custom.css) rather than rewritten: the app toggles between those two spans on its own
   schedule, so editing one of them would be undone whenever it shows the other. With nothing
   remembered for the family, or no guide for this product, Kleep's text shows untouched. Shoes
   have no Kleep button at all: the markup ships a plain text element instead, revealed the same
   way.

   Markup in snippets/size-recommendation.liquid. Re-rendered with the variant picker on variant
   change, so connectedCallback runs again on the fresh element and reads everything afresh.
   ------------------------------------------------------------------------------------------------------------------ */
if (!customElements.get('size-recommendation')) {
  class SizeRecommendation extends HTMLElement {
    static SELECTORS = {
      // The text wrapper of kleep-button and kleep-secondary-button respectively
      textContainer: '.kleep-btn-text, .kleep-secondary-btn-text',
      text: '[data-size-recommendation-text]',
      guide: '[data-size-guide][data-product-handle="%handle%"]',
      matrix: '[data-size-matrix]'
    };

    static CLASSES = { active: 'size-recommendation--active', text: 'size-recommendation__text' };

    static ATTRIBUTES = { text: 'data-size-recommendation-text' };

    static PLACEHOLDERS = { handle: '%handle%', size: '%size%', equivalent: '%equivalent%' };

    static EVENTS = { domReady: 'DOMContentLoaded' };

    connectedCallback () {
      this._abort = new AbortController();
      this._textContainer = this.querySelector(SizeRecommendation.SELECTORS.textContainer);
      this._template = this.dataset.labelTemplate || '';
      this._nativeTemplate = this.dataset.labelTemplateNative || this._template;
      this._handle = this.dataset.productHandle || '';

      // Deferred scripts run in document order, while the document is still "interactive": the
      // size guide (and its memory helpers) may be declared further down the page than this
      // element, so wait for DOMContentLoaded, which fires once every deferred script has run.
      if (!window.SizeGuideMemory && document.readyState !== 'complete') {
        document.addEventListener(SizeRecommendation.EVENTS.domReady, () => this._listen(), { once: true, signal: this._abort.signal });
      } else {
        this._listen();
      }
    }

    disconnectedCallback () {
      this._abort?.abort();
    }

    // Also refreshed when a size gets remembered on this page (an add to cart), so the button
    // reflects the size just added without a reload.
    _listen () {
      const remembered = window.SizeGuideMemory?.EVENTS.remembered;

      if (remembered) {
        document.addEventListener(remembered, () => this.render(), { signal: this._abort.signal });
      }

      this.render();
    }

    render () {
      const recommendation = this._resolve();

      this.classList.toggle(SizeRecommendation.CLASSES.active, Boolean(recommendation));

      if (recommendation) {
        this._ensureText().textContent = this._format(recommendation);
      }
    }

    // Our own text: the plain element the markup ships for shoes, or a span created on first use
    // beside Kleep's own. Hidden by CSS whenever --active is off.
    _ensureText () {
      let text = this.querySelector(SizeRecommendation.SELECTORS.text);

      if (!text && this._textContainer) {
        text = document.createElement('span');
        text.className = SizeRecommendation.CLASSES.text;
        text.setAttribute(SizeRecommendation.ATTRIBUTES.text, '');
        this._textContainer.append(text);
      }

      return text || document.createElement('span');
    }

    _findGuide () {
      if (!this._handle) {
        return null;
      }

      return document.querySelector(SizeRecommendation.SELECTORS.guide.replace(SizeRecommendation.PLACEHOLDERS.handle, CSS.escape(this._handle)));
    }

    _resolve () {
      const memory = window.SizeGuideMemory;
      const guide = this._findGuide();
      const matrix = guide?.querySelector(SizeRecommendation.SELECTORS.matrix);

      if (!memory || !matrix) {
        return null;
      }

      return memory.resolveEntry(matrix, memory.readFamilySize(guide.dataset.family));
    }

    // "(36 FR)" is only worth adding when the shopper's system is not the one the sizes are
    // already shown in — otherwise it would just repeat the size.
    _format ({ size, equivalent }) {
      const template = equivalent.native ? this._nativeTemplate : this._template;

      return template
        .replace(SizeRecommendation.PLACEHOLDERS.size, size)
        .replace(SizeRecommendation.PLACEHOLDERS.equivalent, `${equivalent.value} ${equivalent.system}`);
    }
  }

  customElements.define('size-recommendation', SizeRecommendation);
}
